import type { ToolRunError, ToolRunErrorCode } from '#shared/tools/tool'
import { TOOL_RUN_ERROR_CODES } from '#shared/tools/tool'

/**
 * The two sentences a failed tool run needs — and why they are not the same sentence (MAR-491).
 *
 * `ToolRunError.message` is documented in `shared/tools/tool.ts` as *"Model- and log-facing,
 * English. The UI renders its own copy from `code`."* It is an OPERATOR diagnostic: it names the
 * tool by its internal id (`image_to_word`), it is English whatever language the user reads in, and
 * it is assembled from values nobody chose to show anyone.
 *
 * Two surfaces printed it verbatim anyway. The conversion card rendered it straight into the
 * thread, and the hidden turn that reports the outcome handed it to the model under an instruction
 * to relay the reason — which is how a Spanish-speaking user was shown
 * `image_to_word reads only image files, and "bonilla-scanned" is not one.`
 *
 * The Activity Center had been doing this correctly since MAR-487, so the fix is not new copy: it
 * is one function both paths call, instead of one path having a `switch` and the other having a
 * string concatenation.
 */

type TranslateFn = (key: string, named?: Record<string, unknown>) => string

/**
 * USER-FACING, localized, keyed off `code` — never off the server's `message`.
 *
 * Three distinguishable outcomes, because they ask different things of the reader: the wrong KIND
 * of file (pick another file), a file that could not be READ (this one will not work — the model
 * refused, or the scan is illegible), and anything else (a malfunction).
 */
export function toolRunFailureText(
  t: TranslateFn,
  error: ToolRunError | null | undefined,
  sourceName: string | null | undefined,
): string {
  if (error?.code === 'unsupported_source') {
    return t('tools.runFailure.unsupportedSource', { name: sourceName || '' })
  }
  if (error?.code === 'source_unreadable') return t('tools.runFailure.unreadable')
  return t('tools.runFailure.failed')
}

/**
 * MODEL-FACING, English — per services/agent-web-chat/AGENTS.md every instruction written for a model is English, and
 * the reply language is supplied by the language layer rather than by the string.
 *
 * Says what the server's `message` says WITHOUT the internal tool id, because the model is told to
 * relay this reason and whatever it relays is read by a user. `null` when there is no error, so the
 * caller can omit the clause entirely rather than assert an empty one.
 */
export function toolRunModelReason(
  error: ToolRunError | null | undefined,
  sourceName: string | null | undefined,
): string | null {
  if (!error) return null
  const name = sourceName || 'the file'
  switch (error.code) {
    case 'unsupported_source':
      return `"${name}" is not a kind this tool can read, so nothing was converted.`
    case 'source_unreadable':
      return `The contents of "${name}" could not be read.`
    default:
      return 'The conversion did not complete.'
  }
}

/** Narrow whatever an error carried (e.g. `err.data.code`) to a code the shared copy actually covers. */
export function asToolRunErrorCode(value: unknown): ToolRunErrorCode | null {
  return typeof value === 'string' && (TOOL_RUN_ERROR_CODES as readonly string[]).includes(value)
    ? value as ToolRunErrorCode
    : null
}

export interface SanitizedUpstreamError {
  /** USER-FACING, localized — safe to render verbatim (a card, a toast). */
  userMessage: string
  /** MODEL-FACING, English — safe to hand a model as "the reason". Never the raw diagnostic. */
  modelReason: string
  /** The caught exception's own text. console.error/telemetry ONLY — never render it, never hand
   *  it to a model: it can be an upstream diagnostic verbatim (a docx-service/Gemini error body). */
  diagnostic: string
}

/**
 * Turn "a request into conversion/workflow/tool infra threw" into text that is safe to show a user
 * or hand a model — the general form of `toolRunFailureText`/`toolRunModelReason` (#807).
 *
 * Every such catch had the same shape: the caught exception's `.message` is often an upstream
 * diagnostic verbatim, and "this particular failure carries no coded `ToolRunErrorCode`" was being
 * read as license to fall back to that raw text. It isn't — no code is `code: 'internal'`, exactly
 * like a settled run with no more specific code, and it goes through the same two keyed functions
 * everything else does.
 */
export function sanitizeUpstreamError(
  t: TranslateFn,
  err: unknown,
  sourceName: string | null | undefined,
): SanitizedUpstreamError {
  const code = asToolRunErrorCode((err as { data?: { code?: unknown } })?.data?.code) ?? 'internal'
  const error: ToolRunError = { code, message: '' }
  return {
    userMessage: toolRunFailureText(t, error, sourceName),
    // Always non-null: `toolRunModelReason` only returns null for a null/undefined `error`, and
    // `error` here is always a real object.
    modelReason: toolRunModelReason(error, sourceName)!,
    diagnostic: err instanceof Error ? err.message : String(err ?? 'Unknown error'),
  }
}
